import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { Identifier, useDataProvider } from 'react-admin';

import type { CrmDataProvider } from '@/providers/types';
import { DialogCloseButton } from '@/components/misc/DialogCloseButton';
import { LeadOriginAudio } from './LeadOriginAudio';
import { leadSourceKind, leadSourceLabel } from './leadSourceUtils';

type Props = {
    leadId: Identifier;
    sourcePath: string | null;
    onClose: () => void;
};

export function LeadSourcePreviewDialog({ leadId, sourcePath, onClose }: Props) {
    const dataProvider = useDataProvider<CrmDataProvider>();
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [contentType, setContentType] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!sourcePath) {
            setPreviewUrl(null);
            setContentType('');
            setLoading(false);
            return undefined;
        }

        let objectUrl: string | null = null;
        let cancelled = false;
        setLoading(true);
        setPreviewUrl(null);

        dataProvider.getLeadSource(leadId, sourcePath)
            .then(({ blob, contentType: type }: { blob: Blob; contentType: string }) => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setPreviewUrl(objectUrl);
                setContentType(type);
            })
            .catch(() => {
                if (!cancelled) setPreviewUrl(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [leadId, sourcePath, dataProvider]);

    const title = sourcePath ? leadSourceLabel(sourcePath) : 'Preview';
    const kind = sourcePath ? leadSourceKind(sourcePath) : 'other';
    const showImage = kind === 'image' && !!previewUrl;
    const showAudio =
        !!previewUrl &&
        (kind === 'audio' ||
            contentType.startsWith('audio/') ||
            contentType.startsWith('video/'));

    return (
        <Dialog open={!!sourcePath} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ pr: 6 }}>
                {title}
                <DialogCloseButton onClose={onClose} />
            </DialogTitle>
            <DialogContent
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 240,
                    pb: 2,
                }}
            >
                {loading ? (
                    <CircularProgress size={32} sx={{ my: 4 }} />
                ) : !previewUrl ? (
                    <Typography color="text.secondary">
                        Could not load preview.
                    </Typography>
                ) : showImage ? (
                    <Box
                        component="img"
                        src={previewUrl}
                        alt={title}
                        sx={{
                            display: 'block',
                            maxWidth: '100%',
                            maxHeight: '80vh',
                            objectFit: 'contain',
                        }}
                    />
                ) : showAudio ? (
                    <Box sx={{ width: '100%', maxWidth: 640 }}>
                        <LeadOriginAudio
                            url={previewUrl}
                            contentType={contentType}
                            downloadName={title}
                        />
                    </Box>
                ) : (
                    <Typography color="text.secondary">
                        Preview is not available for this file type. Use Download.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose}>Close</Button>
                <Button
                    variant="contained"
                    startIcon={<DownloadOutlinedIcon />}
                    component="a"
                    href={previewUrl ?? '#'}
                    download={title}
                    disabled={!previewUrl}
                >
                    Download
                </Button>
            </DialogActions>
        </Dialog>
    );
}
